import { type JSX, useEffect, useRef } from 'react';

import type { PublicBroadcast } from '../../broadcasts.js';

export interface BroadcastPanelProps {
  broadcasts: readonly PublicBroadcast[];
  onClose: () => void;
  onDismiss: (id: string) => void;
}

/**
 * Slide-out panel anchored to the LauncherHeader's bell button. Plain
 * text content with URL auto-linkification (no markdown — keeps the XSS
 * surface zero). The whole panel is dismissible by tapping the backdrop
 * or pressing Escape; individual broadcasts dismiss into local-only
 * storage when `dismissible` is true on the wire.
 */
export function BroadcastPanel({
  broadcasts,
  onClose,
  onDismiss,
}: BroadcastPanelProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <>
      <div
        className="broadcast-panel__backdrop"
        onClick={onClose}
        data-testid="broadcast-panel-backdrop"
      />
      <div
        className="broadcast-panel"
        role="dialog"
        aria-label="Broadcasts"
        tabIndex={-1}
        ref={panelRef}
        data-testid="broadcast-panel"
      >
        <header className="broadcast-panel__header">
          <span className="broadcast-panel__title">Notifications</span>
          <button
            type="button"
            className="broadcast-panel__close"
            aria-label="Close"
            onClick={onClose}
            data-testid="broadcast-panel-close"
          >
            ×
          </button>
        </header>
        <div className="broadcast-panel__list">
          {broadcasts.length === 0 ? (
            <p className="broadcast-panel__empty">No new broadcasts.</p>
          ) : (
            broadcasts.map((b) => (
              <BroadcastCard key={b.id} broadcast={b} onDismiss={onDismiss} />
            ))
          )}
        </div>
      </div>
    </>
  );
}

function BroadcastCard({
  broadcast,
  onDismiss,
}: {
  broadcast: PublicBroadcast;
  onDismiss: (id: string) => void;
}): JSX.Element {
  return (
    <article
      className={`broadcast-card broadcast-card--${broadcast.severity}`}
      data-testid={`broadcast-card-${broadcast.id}`}
    >
      <header className="broadcast-card__header">
        <SeverityDot severity={broadcast.severity} />
        <span className="broadcast-card__title">{broadcast.title}</span>
        {broadcast.dismissible && (
          <button
            type="button"
            className="broadcast-card__dismiss"
            aria-label={`Dismiss ${broadcast.title}`}
            onClick={() => onDismiss(broadcast.id)}
          >
            ×
          </button>
        )}
      </header>
      <p className="broadcast-card__body">{renderBodyWithLinks(broadcast.body)}</p>
    </article>
  );
}

function SeverityDot({ severity }: { severity: PublicBroadcast['severity'] }): JSX.Element {
  return <span className={`broadcast-card__dot broadcast-card__dot--${severity}`} aria-hidden />;
}

/**
 * Auto-linkify http(s) URLs inside the plain-text body. React handles
 * escaping for the string nodes; we only ever construct anchor elements
 * around recognized URL substrings, so there's no innerHTML surface.
 */
function renderBodyWithLinks(body: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  const re = /\bhttps?:\/\/[^\s<>"'`]+/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t-${String(i)}`}>{body.slice(last, m.index)}</span>);
    }
    out.push(
      <a
        key={`a-${String(i)}`}
        href={m[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="broadcast-card__link"
      >
        {m[0]}
      </a>,
    );
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < body.length) {
    out.push(<span key={`t-${String(i)}`}>{body.slice(last)}</span>);
  }
  return out;
}
